import * as Sentry from '@sentry/nextjs';

import {
  CreatedGoalSubscriptionModel,
  GetGoalSubscriptionsParamsModel,
  GoalSubscriptionModel,
  GoalSubscriptionStatus
} from 'lib/models/goalSubscriptions';
import { APIRequestLabel } from '../consts';
import { callHttpDelete, callHttpGet, callHttpPost, callHttpPut } from '../http';

const createGoalSubscription = (
  get,
  set
): ((
  goalEntityId: string,
  payload: { goalEntityId: string; priority?: string; deadline?: Date }
) => Promise<CreatedGoalSubscriptionModel>) => {
  return async (
    goalEntityId: string,
    payload: { goalEntityId: string; priority?: string; deadline?: Date }
  ) => {
    await callHttpPost(
      get,
      set,
      APIRequestLabel.CREATE_GOAL_SUBSCRIPTION,
      `/goal_subscriptions`,
      payload
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CREATE_GOAL_SUBSCRIPTION];

    if (apiCallStatus.error) {
      return { success: false };
    }

    // update goals
    let goals = get().goals;
    let creationResponse = apiCallStatus.response;

    let createdSubscription = await getGoalSubscriptionOld(get, set)(creationResponse.id);
    let idx = goals.findIndex((g) => g.entityId === goalEntityId);
    if (idx > -1) {
      goals[idx] = {
        ...goals[idx],
        subscription: createdSubscription
      };
    }

    // update goal
    let goal = get().goal;
    if (goal && goal.entityId === goalEntityId) {
      goal = {
        ...goal,
        subscription: createdSubscription
      };
    }

    set({ goals: goals, goal: goal });
    return creationResponse;
  };
};

const updateGoalSubscription = (get, set) => {
  return async (
    goalEntityId: string,
    goalSubscriptionId: number,
    payload: { priority?: string; deadline?: Date; status?: string }
  ) => {
    await callHttpPut(
      get,
      set,
      APIRequestLabel.UPDATE_GOAL_SUBSCRIPTION,
      `/goal_subscriptions/${goalSubscriptionId}`,
      payload
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.UPDATE_GOAL_SUBSCRIPTION];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    // get the updated goal subscription
    let updatedSubscription = null;
    if (apiCallStatus.response.status !== 'DELETED') {
      updatedSubscription = await getGoalSubscriptionOld(get, set)(goalSubscriptionId);
    }

    // update goals
    let goals = get().goals;
    let idx = goals.findIndex((g) => g.entityId === goalEntityId);
    if (idx > -1) {
      goals[idx] = {
        ...goals[idx],
        subscription: updatedSubscription
      };
    }
    // update goal
    let goal = get().goal;
    if (goal && goal.entityId === goalEntityId) {
      goal = {
        ...goal,
        subscription: updatedSubscription
      };
    }

    set({ goals: goals, goal: goal });
  };
};

/**
 * @deprecated Use getGoalSubscriptions instead
 */
const getGoalSubscriptionsOld = (get, set) => {
  return async (params: GetGoalSubscriptionsParamsModel): Promise<GoalSubscriptionModel[]> => {
    let qParams = {};
    if (params.statuses) {
      qParams['statuses'] = params.statuses;
    }

    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_GOAL_SUBSCRIPTIONS,
      `/goal_subscriptions`,
      {},
      qParams
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_GOAL_SUBSCRIPTIONS];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({ goalSubscriptions: apiCallStatus.response.goalSubscriptions });
    return apiCallStatus.response.goalSubscriptions;
  };
};

/**
 * @deprecated Use getGoalSubscription instead and remember to set the goalSubscription in the store if needed
 */
const getGoalSubscriptionOld = (get, set) => {
  return async (goalSubscriptionId: number): Promise<GoalSubscriptionModel> => {
    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_GOAL_SUBSCRIPTION,
      `/goal_subscriptions/${goalSubscriptionId}`
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_GOAL_SUBSCRIPTION];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    set({ goalSubscription: apiCallStatus.response });
    return apiCallStatus.response;
  };
};

const syncGoalSubscription = (get, set) => {
  return async (
    goalEntityId: string,
    goalSubscriptionId: number,
    status: string,
    previousStatus: string
  ) => {
    let currentStoredGoalSubscription = get().goalSubscription;

    if (currentStoredGoalSubscription && currentStoredGoalSubscription.id !== goalSubscriptionId) {
      //NOTE: We do not want to sync the goal subscription status since the state update
      //does not target our stored goal subscription
      console.debug('Skip the goal subscription update');
      return;
    }

    await callHttpGet(
      get,
      set,
      APIRequestLabel.GET_GOAL_SUBSCRIPTION,
      `/goal_subscriptions/${goalSubscriptionId}`
    );
    let apiCallStatus = get().apiCallStatus[APIRequestLabel.GET_GOAL_SUBSCRIPTION];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }
    const gs = apiCallStatus.response;

    // update goal
    let goal = get().goal;
    if (
      goal &&
      goal.entityId === goalEntityId &&
      (!goal.subscription || goal.subscription.id === goalSubscriptionId)
    ) {
      goal = {
        ...goal,
        subscription: gs
      };
    }

    // trigger the change only if we're certain that the data received from the WS is newer than the one we have
    let goalSubscriptionChangedTo = null;
    if (gs.status === status) {
      // NULL -> ACTIVE
      if (status === GoalSubscriptionStatus.ACTIVE && !previousStatus) {
        goalSubscriptionChangedTo = GoalSubscriptionStatus.ACTIVE;
      }

      // ACTIVE -> COMPLETED
      if (
        status === GoalSubscriptionStatus.COMPLETED &&
        previousStatus === GoalSubscriptionStatus.ACTIVE
      ) {
        goalSubscriptionChangedTo = GoalSubscriptionStatus.COMPLETED;
      }
    }

    set({
      goal: goal,
      goalSubscriptionChangedTo: goalSubscriptionChangedTo,
      goalSubscription: gs
    });
  };
};

const cancelPendingSubscription = (get, set) => {
  return async (goalSubscriptionId: number) => {
    await callHttpDelete(
      get,
      set,
      APIRequestLabel.CANCEL_PENDING_SUBSCRIPTION,
      `/goal_subscriptions/${goalSubscriptionId}`
    );

    let apiCallStatus = get().apiCallStatus[APIRequestLabel.CANCEL_PENDING_SUBSCRIPTION];
    if (apiCallStatus.error) {
      Sentry.captureMessage(JSON.stringify(apiCallStatus.error));
      return;
    }

    // Update local state if needed
    set({ goalSubscription: null });
  };
};

export {
  cancelPendingSubscription,
  createGoalSubscription,
  getGoalSubscriptionOld,
  getGoalSubscriptionsOld,
  syncGoalSubscription,
  updateGoalSubscription
};
